import { memo, useEffect, useState } from "react";
import {
  Grid,
  Flex,
  Text,
  Skeleton,
  useMantineTheme,
  Table,
  Accordion,
  Tabs,
} from "@mantine/core";
import ActionCard from "src/components/ActionCard";
import {
  ClientChangeRecordsQuery,
  useClientChangeRecordsQuery,
  useClientChangeOlderRecordsLazyQuery,
} from "src/generated/graphql";

interface ClientOverProps {
  clientId: string;
}

type IClientLogDataDetails =
  ClientChangeRecordsQuery["clientChangeRecordsMany"];

type IClientLogDataSingle = {
  id: string;
  modalName: string;
  author: string;
  description: string;
  isJSON: boolean;
  oldValues?: string | null | undefined;
  newValues?: string | null | undefined;
  createdDate: any;
};
interface FilteredData {
  last7Days: IClientLogDataDetails;
  last30Days: IClientLogDataDetails;
  olderData: IClientLogDataDetails;
}

const columnNames = ["Date", "Author"];

function filterDataByDays(data: IClientLogDataDetails) {
  const result: FilteredData = {
    last7Days: [],
    last30Days: [],
    olderData: [],
  };
  let today = new Date();

  // Calculate the cutoff dates
  let cutoffDate7Days = new Date();
  cutoffDate7Days.setDate(today.getDate() - 6);

  let cutoffDate30Days = new Date();
  cutoffDate30Days.setDate(today.getDate() - 30);

  // Filter the dataArray based on the date
  data?.forEach(function (item) {
    let itemDate = new Date(item.createdDate);

    if (itemDate >= cutoffDate7Days) {
      result.last7Days.push(item);
    } else if (itemDate >= cutoffDate30Days && itemDate < cutoffDate7Days) {
      result.last30Days.push(item);
    } else {
      result.olderData.push(item);
    }
  });
  return result;
}

const ClientLogs = ({ clientId }: ClientOverProps) => {
  const theme = useMantineTheme();
  const [activeTab, setActiveTab] = useState<string | null>("60");
  const [clientLogsList, setClientLogsList] = useState<FilteredData>({
    last7Days: [],
    last30Days: [],
    olderData: [],
  });
  const [selectedLogDetails, setSelectedLogDetails] =
    useState<IClientLogDataSingle | null>(null);

  const { data } = useClientChangeRecordsQuery({
    variables: {
      clientId: clientId,
    },
    onCompleted: (data) => {
      setClientLogsList(filterDataByDays(data?.clientChangeRecordsMany));
    },
    fetchPolicy: "network-only",
  });

  const [getOlderLogs] = useClientChangeOlderRecordsLazyQuery({
    onCompleted(data) {
      setClientLogsList((prev) => ({
        ...prev,
        olderData: [...data?.clientChangeRecordsOlderMany],
      }));
    },
  });

  useEffect(() => {
    getOlderLogs({
      variables: {
        clientId,
        filter: parseInt(activeTab!),
      },
    });
  }, [activeTab]);

  const changeDetails = (
    oldValues: string | null | undefined,
    newValues: string | null | undefined
  ) => {
    if (!oldValues || !newValues) return;

    let color = theme.colors.secondaryTextColor[6];

    let parsedOldValues = JSON.parse(oldValues);
    let parsedNewValues = JSON.parse(newValues);
    return (
      <>
        <Text color={color} fw={"bold"}>
          Details :
        </Text>
        <Flex direction="column" mx={20} my={20}>
          {Object.entries(parsedNewValues).map(([key, val]) => {
            if (parsedOldValues[key] !== val) {
              return (
                <Flex justify="space-between" align="center">
                  <Text color={color} fw={"bold"}>
                    {key} :
                  </Text>
                  <Text
                    color={color}
                  >{`${parsedOldValues[key]} -> ${val}`}</Text>
                </Flex>
              );
            }
          })}
        </Flex>
      </>
    );
  };

  const HistoryTable = (data: IClientLogDataDetails) => {
    return (
      <Table
        sx={{
          thead: {
            tr: {
              th: {
                color: theme.colors.secondaryTextColor[6],
              },
            },
          },
          tbody: {
            tr: {
              "&:nth-of-type(odd)": {
                color: theme.colors.secondaryTextColor[6],
                backgroundColor: theme.colors.secondary[6],
              },
              "&:nth-of-type(even)": {
                color: theme.colors.secondaryTextColor[4],
                backgroundColor: theme.colors.secondary[4],
              },
              "&:hover": {
                backgroundColor: theme.colors.secondary[2],
              },
              td: {
                borderTop: `1px solid ${theme.colors.secondary[2]}`,
              },
            },
          },
        }}
      >
        <thead>
          <tr>
            {columnNames.map((column) => (
              <th key={column}>{column}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data?.map((el, index) => {
            return (
              <tr key={index} onClick={() => setSelectedLogDetails(el)}>
                <td>
                  {new Date(el.createdDate).toDateString()} <br />{" "}
                  {new Date(el.createdDate).toLocaleTimeString()}{" "}
                </td>
                <td>{el.author}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  };

  return (
    <Grid>
      <Grid.Col sm={6} xs={12}>
        <ActionCard title="Account Logs">
          {data && (
            <Accordion
              multiple
              defaultValue={["recent", "upcomming"]}
              transitionDuration={500}
              styles={{
                item: {
                  border: `1px solid ${theme.colors.primary[6]}`,
                  marginTop: 15,
                  borderRadius: "4px",
                  overflow: "hidden",
                },
                control: {
                  background: theme.colors.primary[6],
                  color: theme.colors.primaryTextColor[6],
                  padding: "5px 13px",
                  "&:hover": {
                    backgroundColor: `${theme.colors.primary[4]} !important`,
                  },
                },
                label: {
                  fontWeight: 500,
                  fontSize: 16,
                },
                panel: {
                  background: theme.colors.secondary[6],
                },
              }}
            >
              <Accordion.Item value="recent" mah={500}>
                <Accordion.Control>Recent</Accordion.Control>
                <Accordion.Panel>
                  {HistoryTable(clientLogsList.last7Days)}
                </Accordion.Panel>
              </Accordion.Item>

              <Accordion.Item value="last30Days" mah={500}>
                <Accordion.Control>Last 30 days</Accordion.Control>
                <Accordion.Panel>
                  {HistoryTable(clientLogsList.last30Days)}
                </Accordion.Panel>
              </Accordion.Item>

              <Accordion.Item value="older" mah={500}>
                <Accordion.Control>Older</Accordion.Control>
                <Accordion.Panel>
                  <Tabs
                    value={activeTab}
                    onTabChange={setActiveTab}
                    styles={{
                      tabsList: {
                        color: theme.colors.secondaryTextColor[6],
                        padding: "0 15px",
                      },
                      tab: {
                        marginBottom: -2.5,
                        fontWeight: 500,
                        borderBottomWidth: 3,
                      },
                      panel: {
                        padding: 15,
                      },
                    }}
                  >
                    <Tabs.List>
                      {[
                        { key: "60", label: "60 Days" },
                        { key: "90", label: "90 Days" },
                        { key: "120", label: "120 Days" },
                      ].map((e) => (
                        <Tabs.Tab
                          key={e.key}
                          value={e.key}
                          style={{
                            color: theme.colors.secondaryTextColor[2],
                          }}
                          sx={{
                            "&:hover": {
                              color: `${theme.colors.primary[4]} !important`,
                              borderColor: `${theme.colors.primary[4]} !important`,
                            },
                          }}
                        >
                          {e.label}
                        </Tabs.Tab>
                      ))}
                      {/* <Tabs.Tab value="60">Past 60 Days</Tabs.Tab>
                      <Tabs.Tab value="90">Past 90 Days</Tabs.Tab>
                      <Tabs.Tab value="120">Past 120 Days</Tabs.Tab> */}
                    </Tabs.List>
                  </Tabs>
                  {HistoryTable(clientLogsList.olderData)}
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          )}
        </ActionCard>
      </Grid.Col>

      <Grid.Col sm={6} xs={12}>
        <ActionCard title="Details">
          {selectedLogDetails ? (
            <>
              <Flex justify="space-between" align="center" my={10}>
                <Text color={theme.colors.secondaryTextColor[6]} fw={"bold"}>
                  Author :
                </Text>
                <Text color={theme.colors.secondaryTextColor[6]}>
                  {selectedLogDetails.author}
                </Text>
              </Flex>
              <Flex justify="space-between" align="center" my={10}>
                <Text color={theme.colors.secondaryTextColor[6]} fw={"bold"}>
                  Date :
                </Text>
                <Text color={theme.colors.secondaryTextColor[6]}>
                  {new Date(selectedLogDetails.createdDate).toDateString()}
                </Text>
              </Flex>
              <Flex justify="space-between" align="center" my={10}>
                <Text color={theme.colors.secondaryTextColor[6]} fw={"bold"}>
                  Change :
                </Text>
                <Text color={theme.colors.secondaryTextColor[6]}>
                  {selectedLogDetails.modalName}
                </Text>
              </Flex>

              <Flex justify="space-between" my={10}>
                <Text
                  color={theme.colors.secondaryTextColor[6]}
                  fw={"bold"}
                  mr={50}
                >
                  Description:
                </Text>
                <Text color={theme.colors.secondaryTextColor[6]} maw={500}>
                  {selectedLogDetails.description}
                </Text>
              </Flex>
              {selectedLogDetails.isJSON &&
                changeDetails(
                  selectedLogDetails?.oldValues,
                  selectedLogDetails?.newValues
                )}
            </>
          ) : (
            <Text
              color={theme.colors.secondaryTextColor[6]}
              fw={"bold"}
              mr={50}
              align="center"
            >
              No Log Selected
            </Text>
          )}
        </ActionCard>
      </Grid.Col>
    </Grid>
  );
};

export default memo(ClientLogs);
